import {skipToken} from '@reduxjs/toolkit/query';

import {useSelector} from 'react-redux';

import {defaultTo, find, isNil, map, reject} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetBankListQuery, useGetBranchQuery, useGetTenantQuery} from '@dms/api/core';
import {selectActiveBranchId} from '@dms/api/features';
import {BankAccountData, BankAccountResponseBody, BankListResponseBody} from '@dms/api/shared';

import {Nullish} from 'shared/web';

import {
  getAftersalesBankAccounts,
  getDefaultAftersalesBankAccount,
} from '../utils/getAftersalesBankAccounts';
import {getDefaultSalesBankAccount, getSalesBankAccounts} from '../utils/getSalesBankAccounts';
import {useCurrencies} from './useCurrencies';

type Code = string | Nullish;

/** Pass null while an explicit branch is unresolved; omit to use the active branch. */
export function useBank(branchIdOverride?: string | Nullish) {
  const {getCurrencyName} = useCurrencies();
  const {
    data: tenantData,
    isLoading: isLoadingTenant,
    isError: isTenantError,
  } = useGetTenantQuery();
  const {
    data,
    isLoading: isLoadingBankList,
    isError: isBankListError,
  } = useGetBankListQuery({countryCode: tenantData?.country}, {skip: !tenantData});
  const activeBranchId = useSelector(selectActiveBranchId);
  const isExplicitBranch = branchIdOverride !== undefined;
  const branchId = isExplicitBranch ? branchIdOverride : activeBranchId;
  const {
    data: lastBranchData,
    currentData: currentBranchData,
    isFetching: isFetchingBranchData,
    isError,
  } = useGetBranchQuery(branchId ? {branchId} : skipToken);
  // An explicit branch must never fall back to another branch's cached result, otherwise the
  // caller would read accounts of the branch it just navigated away from. The active-branch
  // path keeps `data` so existing callers still render the previous branch while a new one loads.
  const branchData = isExplicitBranch ? currentBranchData : lastBranchData;
  const isLoadingBranchData = isFetchingBranchData && isNil(branchData);

  const getBankByCode = (code: Code): BankListResponseBody | undefined =>
    find((bank) => bank.code === code, data ?? []);

  const composeBankAccount = (bankAccountData: Partial<BankAccountData> | Nullish): string | null =>
    reject(isNilOrEmpty, [
      bankAccountData?.ownerName,
      bankAccountData?.number,
      getBankByCode(bankAccountData?.bankCode)?.name,
      getCurrencyName(bankAccountData?.currency),
      bankAccountData?.swiftBic,
      bankAccountData?.iban,
    ]).join(', ');

  const bankOptions =
    data?.map((g) => ({
      label: `${g.name} (${g.code})`,
      value: g.code,
    })) ?? [];

  const getBankAccountName = (bankAccount: BankAccountResponseBody) =>
    reject(isNilOrEmpty, [
      bankAccount.accountName,
      bankAccount.accountNumber,
      bankAccount.bankName,
      getCurrencyName(bankAccount.currency),
    ]).join(', ');

  const bankAccountOptions = map(
    (account) => ({
      label: getBankAccountName(account),
      value: account.accountNumber ?? '',
    }),
    branchData?.billingInformation?.bankAccounts ?? []
  );

  const primaryBankAccount = branchData?.billingInformation?.bankAccounts?.find(
    (bankAcc) => bankAcc.isPrimary === true
  );

  const primaryBankAccountOption = primaryBankAccount
    ? {
        label: getBankAccountName(primaryBankAccount),
        value: primaryBankAccount.accountNumber ?? '',
      }
    : undefined;

  const getBankAccountByNumber = (bankNumber: string) =>
    find(
      (bank) => bank.accountNumber === bankNumber,
      branchData?.billingInformation?.bankAccounts ?? []
    );

  const allBankAccounts = branchData?.billingInformation?.bankAccounts ?? [];

  const salesBankAccounts = getSalesBankAccounts(allBankAccounts);

  const salesBankAccountOptions = map(
    (account) => ({
      label: getBankAccountName(account),
      value: account.accountNumber ?? '',
    }),
    salesBankAccounts
  );

  const getSalesDefaultBankAccount = (currency?: string) =>
    getDefaultSalesBankAccount(allBankAccounts, currency);

  const aftersalesBankAccounts = getAftersalesBankAccounts(allBankAccounts);

  const aftersalesBankAccountOptions = map(
    (account) => ({
      label: getBankAccountName(account),
      value: account.accountNumber ?? '',
    }),
    aftersalesBankAccounts
  );

  const getAftersalesDefaultBankAccount = (currency?: string) =>
    getDefaultAftersalesBankAccount(allBankAccounts, currency);

  const isLoading = isLoadingTenant || isLoadingBankList || isLoadingBranchData;

  return {
    bankOptions,
    bankAccountOptions,
    salesBankAccountOptions,
    getSalesDefaultBankAccount,
    aftersalesBankAccountOptions,
    getAftersalesDefaultBankAccount,
    primaryBankAccountOption,
    getBank: (code: Code) => defaultTo(null, getBankByCode(code)),
    getBankName: (code: Code) => defaultTo(null, getBankByCode(code)?.name),
    composeBankAccount,
    getBankAccountName,
    getBankAccountByNumber,
    isLoading,
    isError,
    /** The bank list behind bank names failed; `isError` covers only the branch accounts. */
    isBankListError: isTenantError || isBankListError,
  };
}
